import {Box, Flex, useStyleConfig} from '@chakra-ui/react'
import {keyframes} from '@emotion/react'
import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import CustomLink from '../custom-link'
import {useGetSovraHeader} from '../../custom-api/hooks/use-get-sovra-header'
import {safeBuildUrl} from '../../utils/contentstack-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const scrollLeft = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
`

const SovraHeader = () => {
    const {data} = useGetSovraHeader()
    const {buildUrl} = useMultiSite()
    const styles = useStyleConfig('SovraHeader')
    const groupLinkSchema = data?.grouplink
    const viewportRef = useRef(null)
    const groupRef = useRef(null)
    const [motion, setMotion] = useState({isOverflowing: false, duration: 0})

    useEffect(() => {
        const viewport = viewportRef.current
        const group = groupRef.current
        if (!viewport || !group) return

        const measure = () => {
            const width = group.getBoundingClientRect().width
            setMotion({
                isOverflowing: width > viewport.clientWidth,
                duration: (width + 24) / 10,
            })
        }

        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(viewport)
        observer.observe(group)
        return () => observer.disconnect()
    }, [groupLinkSchema])

    if (!groupLinkSchema || groupLinkSchema.length === 0) {
        return null
    }

    return (
        <Box id="sovra-header" {...styles.headerContainer}>
            <Flex ref={viewportRef} {...styles.viewport}>
                <Flex
                    {...styles.track}
                    mx={motion.isOverflowing ? 0 : 'auto'}
                    pr={motion.isOverflowing ? styles.track.paddingRight : 0}
                    animation={
                        motion.isOverflowing
                            ? `${scrollLeft} ${motion.duration}s linear infinite`
                            : 'none'
                    }
                >
                    {(motion.isOverflowing ? [0, 1] : [0]).map((copyIndex) => (
                        <Flex
                            key={copyIndex}
                            ref={copyIndex === 0 ? groupRef : undefined}
                            aria-hidden={copyIndex === 1 ? true : undefined}
                            {...styles.group}
                        >
                            {groupLinkSchema.map((button, index) => {
                                const isExternalUrl = !!button?.is_external_url
                                const href = button?.header_link?.href
                                const title = button?.header_link?.title
                                const isLinkActive = !!href
                                const buttonHref = isExternalUrl
                                    ? href ?? ''
                                    : safeBuildUrl(href, buildUrl)
                                return (
                                    <CustomLink
                                        key={index}
                                        asButton={isLinkActive}
                                        variant="unstyled"
                                        data-testid={`${_.kebabCase(title)}-current-button${
                                            copyIndex === 1 ? '-duplicate' : ''
                                        }`}
                                        tabIndex={copyIndex === 1 ? -1 : undefined}
                                        isExternal={isExternalUrl && isLinkActive}
                                        href={buttonHref}
                                        {...styles.link}
                                    >
                                        {title}
                                    </CustomLink>
                                )
                            })}
                        </Flex>
                    ))}
                </Flex>
            </Flex>
        </Box>
    )
}

SovraHeader.getTemplateName = () => 'sovra-header'

export default SovraHeader
